import { createFileRoute, useNavigate, useLocation, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { api, ApiError, isProviderError, newIntentNonce } from '@/lib/api';
import type { Subscription } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { BottomSheet, ConfirmDialog } from '@/components/ui/dialog';
import {
  Smartphone, Laptop, Snowflake, Gift, Plus, Loader2, Copy, Check, Shield, Zap, Store,
  Sparkles, ArrowUp, Wallet, Globe2, ChevronRight, RefreshCw, PlusSquare, Users, CalendarClock, Clock, Send,
  ListChecks,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getWebApp, haptic, openInvoiceUrl } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import {
  Skeleton,
  SkeletonCard,
  SkeletonGrid,
  SkeletonLine,
  SkeletonPage,
} from '@/components/ui/skeleton';
import { IconChip, SectionLabel, ProgressBar, StatusPill, ActionTile } from '@/components/ui/calm';
import { ProviderPills } from '@/components/ui/provider-pills';
import { cn } from '@/lib/utils';
import { daysLeft as calcDaysLeft, daysProgressRatio, trafficPercent } from '@/lib/subscription-progress';
import { toast } from 'sonner';
import { shortDate } from '@/lib/date';
import { telegramShareUrl } from '@/lib/clipboard';
import { formatWholeMinor, groupDigits, rub } from '@/lib/currency';
import { WITHDRAW_QUERY_KEY } from '@/lib/withdraw';
import { Flag } from '@/components/ui/flag';

// Сигнал «проскроллить к блоку рефералки» приходит из профиля через router-state
// (а НЕ через URL-hash — hash занят телеграм-параметрами bot_id/initData, см. lib/api.ts,
// перезаписывать его нельзя). HistoryState пустой и расширяемый — аугментируем.
declare module '@tanstack/history' {
  interface HistoryState {
    scrollTo?: 'referral';
    /** Профиль просит сразу открыть шит заморозки (ссылка «Заморозить подписку»). */
    openSheet?: 'freeze';
  }
}

export const Route = createFileRoute('/me')({
  component: Dashboard,
});

/**
 * Пробную подписку заморозить нельзя: AdaptGroup на freeze триала отвечает успехом,
 * но дни продолжают списываться. Раньше клиент видел «Заморожена» и терял пробный
 * период. Бэк теперь отвечает 409 `app.subscriptions.trial_cannot_freeze`, а UI не
 * даёт даже открыть шит.
 */
const TRIAL_FREEZE_NOTE_KEY = 'Trial freeze note';

export function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const referralRef = useRef<HTMLDivElement>(null);

  // Все активные подписки в текущем боте (юзер мог купить 2+). Выбранная общая
  // для всех экранов, см. lib/use-selected-subscription.ts.
  // refetchOnMount:'always' — persister в sessionStorage может вернуть stale-данные
  // (например после freeze/unfreeze в прошлой сессии), нам нужен свежий статус с сервера.
  const { subs, sub, select, isPending: subPending } = useSelectedSubscription({
    refetchOnMount: 'always',
  });
  // Истёкшие за 30 дней: без живой подписки показываем карточку «Продлить». Раньше
  // истёкшая пропадала, и человек мог только купить новую, с новой ссылкой.
  const { data: expiredSubs } = useQuery({
    queryKey: ['subs-expired'],
    queryFn: () => api.expiredSubscriptions(),
    enabled: !subPending && !sub,
    staleTime: 60_000,
  });

  // У «Навсегда» превью устройств на главной не грузим: список стоит два запроса к
  // поставщику, а лимит у него общий с покупками всей платформы. Список на /devices.
  const { data: devices } = useQuery({
    queryKey: ['devices', sub?.id],
    queryFn: () => (sub ? api.devices(sub.id) : Promise.resolve([])),
    enabled: !!sub && sub.supplier !== 'scvpn',
    placeholderData: keepPreviousData,
  });

  const { data: profileData } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.profile(),
    placeholderData: keepPreviousData,
  });
  // Кнопки партнёра для раздела «Мои подписки» (идея #10). Блок необязательный,
  // до ответа его просто нет: ложного пустого состояния тут не бывает.
  const { data: info } = useQuery({
    queryKey: ['client-info'],
    queryFn: () => api.info(),
    placeholderData: keepPreviousData,
  });
  const customSubButtons = info?.custom_buttons?.subs ?? [];
  const referralLink = profileData?.referral_link ?? null;
  const refL1Percent = profileData?.referral_l1_percent ?? 15;
  const refL2Percent = profileData?.referral_l2_percent ?? 5;
  const refInvited = profileData?.referrals_invited ?? 0;
  // Реф-программа выключена партнёром → прячем блок (бонус не начислится, не вводим в заблуждение).
  const referralEnabled = profileData?.referral_enabled ?? false;

  const copyReferral = async () => {
    if (!referralLink) {
      toast.error(t('Referral link missing'));
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referralLink);
        haptic('success');
        toast.success(t('Link copied'));
        return;
      }
      throw new Error('no clipboard');
    } catch {
      getWebApp()?.openTelegramLink(
        telegramShareUrl(referralLink, t('Referral share text')),
      );
    }
  };

  const [freezeOpen, setFreezeOpen] = useState(false);
  const [trafficOpen, setTrafficOpen] = useState(false);
  // Докупка к «Навсегда»: какой лист открыт (трафик обхода или +15 устройств).
  const [addonKind, setAddonKind] = useState<'traffic' | 'devices' | null>(null);
  const autoRenew = sub?.autoRenew ?? false;

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  // Пришли из профиля по тапу на плитку «Реферальная программа» → плавно
  // подкручиваем к блоку «Пригласи друга». Блок рендерится только при
  // referralEnabled; если выключен — скролл просто no-op (профиль и не ведёт сюда).
  useEffect(() => {
    if (location.state.scrollTo !== 'referral' || !referralEnabled) return;
    const el = referralRef.current;
    if (!el) return;
    const id = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(id);
  }, [location.state.scrollTo, referralEnabled, sub?.id]);

  // Выбранная подписка помнится между заходами, и её плитка может стоять третьей:
  // на узком экране лента открывалась с первыми двумя, а выбранной не было видно.
  // Прокручиваем саму ленту, не страницу.
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = pickerRef.current;
    const chip = strip?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!strip || !chip) return;
    const s = strip.getBoundingClientRect();
    const c = chip.getBoundingClientRect();
    if (c.left < s.left || c.right > s.right) strip.scrollLeft += c.left - s.left - 16;
  }, [sub?.id, subs.length]);

  // Из профиля по ссылке «Заморозить подписку» — открываем шит сразу, а не бросаем
  // человека на главную гадать, куда нажимать. Без подписки замораживать нечего.
  // У триала шит не открываем вовсе (см. TRIAL_FREEZE_NOTE_KEY), объясняем тостом.
  useEffect(() => {
    if (location.state.openSheet !== 'freeze' || !sub) return;
    if (sub.isTrial && sub.status !== 'frozen') {
      toast.info(t(TRIAL_FREEZE_NOTE_KEY), {
        duration: 6000,
        action: { label: t('Upgrade action'), onClick: () => navigate({ to: '/upgrade' }) },
      });
      return;
    }
    setFreezeOpen(true);
  }, [location.state.openSheet, sub, navigate]);

  const { mutate: toggleRenewMutation } = useMutation({
    mutationFn: ({ id, enabled, days }: { id: string; enabled: boolean; days: number | null }) =>
      api.toggleAutoRenew(id, enabled, days),
    // Тумблер на главной читает autoRenew из ['subs'], поэтому оптимистично правим
    // именно этот кэш — иначе свитч не реагирует на тап до refetch'а.
    onMutate: async ({ id, enabled, days }) => {
      await qc.cancelQueries({ queryKey: ['subs'] });
      const prevAll = qc.getQueryData<Subscription[]>(['subs']);
      qc.setQueryData<Subscription[]>(['subs'], (list) =>
        list
          ? list.map((s) =>
              s.id === id ? { ...s, autoRenew: enabled, autoRenewDays: days } : s,
            )
          : list,
      );
      return { prevAll };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevAll !== undefined) qc.setQueryData(['subs'], ctx.prevAll);
      haptic('error');
      toast.error(t('Save failed'));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['subs'] });
    },
  });

  const autoRenewDays = sub?.autoRenewDays ?? null;

  const toggleAutoRenew = (val: boolean) => {
    toast.success(val ? t('Auto-renew on') : t('Auto-renew off'));
    if (sub) toggleRenewMutation({ id: sub.id, enabled: val, days: autoRenewDays });
  };

  /** Срок автопродления: период тарифа или свой. Меняется, не трогая тумблер. */
  const setAutoRenewDays = (days: number | null) => {
    if (sub) toggleRenewMutation({ id: sub.id, enabled: sub.autoRenew, days });
  };

  const { mutate: freeze, isPending: freezing } = useMutation({
    mutationFn: async (): Promise<{ wasFrozen: boolean }> => {
      if (!sub) throw new Error('no sub');
      const subId = sub.id;
      // Спрашиваем у сервера актуальный статус — не доверяем локальному cache
      // (sessionStorage persister и keepPreviousData могут отдавать stale-данные).
      const fresh = await api.allSubscriptions();
      const current = fresh.find((s) => s.id === subId);
      const wasFrozen = current ? current.status === 'frozen' : sub.status === 'frozen';
      if (wasFrozen) await api.unfreezeSubscription(subId);
      else await api.freezeSubscription(subId);
      return { wasFrozen };
    },
    onMutate: async () => {
      if (!sub) return { prevAll: undefined };
      const subId = sub.id;
      await qc.cancelQueries({ queryKey: ['subs'] });
      const prevAll = qc.getQueryData<Subscription[]>(['subs']);
      const flip = (s: Subscription): Subscription => ({
        ...s,
        status: s.status === 'frozen' ? 'active' : 'frozen',
      });
      qc.setQueryData<Subscription[]>(['subs'], (list) =>
        list ? list.map((s) => (s.id === subId ? flip(s) : s)) : list,
      );
      // Закрываем диалог сразу — UI уже показывает новое состояние оптимистично.
      setFreezeOpen(false);
      return { prevAll };
    },
    onSuccess: ({ wasFrozen }) => {
      haptic('success');
      toast.success(wasFrozen ? t('Sub unfrozen') : t('Sub frozen'));
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prevAll !== undefined) qc.setQueryData(['subs'], ctx.prevAll);
      haptic('error');
      // Триал мог проскочить локальный guard (stale-кеш, старая вкладка) — бэк отдаёт
      // 409 с этим кодом, показываем тот же понятный текст, а не «не получилось».
      const errBody = (err instanceof ApiError ? err.body : null) as
        | { error?: { code?: string } }
        | null;
      if (errBody?.error?.code === 'app.subscriptions.trial_cannot_freeze') {
        toast.info(t(TRIAL_FREEZE_NOTE_KEY), { duration: 6000 });
        qc.refetchQueries({ queryKey: ['subs'] });
        return;
      }
      toast.error(t('Refreshing status'));
      // На случай рассинхрона: после fail-а сразу подтягиваем свежий статус с сервера.
      qc.refetchQueries({ queryKey: ['subs'] });
    },
    onSettled: async () => {
      // Force-refetch — invalidate сам по себе ставит query stale, но refetch ждать
      // не будет; при медленной сети UI остаётся в optimistic-состоянии вплоть до
      // следующего mount'а. await refetchQueries синхронно подтягивает свежий статус.
      await qc.refetchQueries({ queryKey: ['subs'] });
    },
  });

  if (subPending && !sub) return <DashboardSkeleton />;
  if (!sub) {
    return (
      <EmptyHome
        onPickPlan={() => navigate({ to: '/plans' })}
        expired={expiredSubs?.[0] ?? null}
      />
    );
  }

  // Источник истины — days_left с бэка: он учитывает заморозку (у замороженной
  // подписки end_date стоит на месте, локальный расчёт от Date.now() утекал бы).
  // Локальный calcDaysLeft остаётся фолбэком для mock-данных.
  const daysLeft = sub.daysLeft ?? calcDaysLeft(sub.expiresAt);
  // Шкала — период тарифа, а не захардкоженные 30 дней. Продлившийся заранее видит
  // полную полосу, а не «81 / 30», где остаток больше шкалы и читается как поломка.
  const progressRatio = daysProgressRatio(daysLeft, sub.days || daysLeft);
  // Дни — зелёный градиент по дизайну; число окрашиваем в предупреждающий цвет при <=7д.
  // Последние часы подписки: бэк отдаёт 0 полных дней, и красный «0» читался как
  // «уже закончилась», хотя VPN ещё работает.
  const daysLeftLabel = daysLeft === 0 && new Date(sub.expiresAt).getTime() > Date.now() ? '<1' : String(daysLeft);
  const daysTextColor = daysLeft <= 3 ? 'text-[#f0656a]' : daysLeft <= 7 ? 'text-[#f0b04e]' : 'text-[#5fe3ad]';

  const trafficPct = trafficPercent(sub.trafficUsedGb, sub.trafficTotalGb);
  // Раздел «Навсегда» (FranCenter): сама подписка и её пробник.
  const lifetimeSection = sub.supplier === 'scvpn';
  const isFrozen = sub.status === 'frozen';
  // Триал морозить нельзя; унаследованному «замороженному» триалу разморозку оставляем.
  const freezeBlocked = sub.isTrial && !isFrozen;
  const openFreeze = () => {
    if (freezeBlocked) {
      haptic('error');
      toast.info(t(TRIAL_FREEZE_NOTE_KEY), {
        duration: 6000,
        action: { label: t('Upgrade action'), onClick: () => navigate({ to: '/upgrade' }) },
      });
      return;
    }
    setFreezeOpen(true);
  };

  return (
    <>
      <div className="p-4 space-y-5 pb-24">
        {/* Picker над карточкой если у клиента 2+ подписки.

            Лента с прокруткой вбок, а не сетка в две колонки: с третьей подписки
            сетка отъедала экран рядами, и до карточки текущего тарифа нужно было
            скроллить. Здесь высота одна при любом количестве.

            `-mx-4 px-4` — лента режется по краям экрана, а не по отступу колонки:
            иначе крайние плитки обрываются на полпути и выглядят сломанными. */}
        {subs.length > 1 && (
          <div ref={pickerRef} className="-mx-4 px-4 -mb-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [-webkit-overflow-scrolling:touch]">
            <div className="flex gap-2 w-max snap-x snap-mandatory">
              {subs.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => select(s.id)}
                  aria-pressed={s.id === sub.id}
                  className={cn(
                    'snap-start shrink-0 w-[152px] px-3 py-2.5 rounded-2xl text-xs font-bold text-left transition-all active:scale-[0.98]',
                    s.id === sub.id ? 'calm-cta' : 'calm-card text-foreground',
                  )}
                >
                  {/* Две строки: у спецтарифов общее начало («Белые Списки | …»), и
                      в одну строку плитки читались одинаково. */}
                  <div className="line-clamp-2 leading-tight">{s.planName}</div>
                  <div className={cn('text-[10px] mt-0.5 flex items-center gap-1', s.id === sub.id ? 'opacity-90' : 'text-[#8a8c93]')}>
                    {s.status === 'frozen' ? (
                      <><Snowflake size={11} /> {t('Frozen')}</>
                    ) : s.isLifetime ? (
                      t('Lifetime forever')
                    ) : (
                      t('Until date', { date: shortDate(s.expiresAt, i18n.language) })
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <GiveawayTile />

        {/* Карточка тарифа */}
        <Card className="calm-card-hero overflow-hidden relative">
          <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-primary blur-[80px] opacity-[0.18] pointer-events-none" />
          <CardContent className="p-6 relative z-10">
            <div className="flex justify-between items-start mb-7">
              <div className="min-w-0">
                <SectionLabel className="mb-1.5">{t('Current Plan')}</SectionLabel>
                {/* Кегль по длине названия: «14 дней Стандарт» в 28px не влезал и
                    обрывался многоточием на «Станд…». Длинные партнёрские названия
                    переносим на вторую строку, дальше уже режем. */}
                <h2
                  className={cn(
                    'font-extrabold tracking-tight text-foreground line-clamp-2',
                    sub.planName.length > 22
                      ? 'text-[20px] leading-tight'
                      : sub.planName.length > 15
                        ? 'text-[23px] leading-tight'
                        : 'text-[28px]',
                  )}
                >
                  {sub.planName}
                </h2>
                {sub.isTrial && (
                  <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[rgba(56,224,165,0.16)] text-money border border-[rgba(56,224,165,0.3)]">
                    <Gift className="w-2.5 h-2.5" /> {t('Trial period')}
                  </span>
                )}
              </div>
              <StatusPill status={isFrozen ? 'frozen' : 'active'} />
            </div>

            <div className="space-y-5 mb-6">
              {sub.isLifetime ? (
                // «Навсегда»: срока нет, дата у поставщика 2100 год. Полоса дней тут
                // врала бы, поэтому одна строка.
                <div className="flex justify-between text-sm">
                  <span className="text-[#8a8c93] font-medium">{t('Term')}</span>
                  <span className="font-bold text-[#5fe3ad]">∞ {t('Lifetime forever')}</span>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between text-sm mb-2.5">
                    <span className="text-[#8a8c93] font-medium">{t('Days left')}</span>
                    {/* Только остаток, без знаменателя: период тарифа тут ни при чём, а
                        после продлений остаток его перерастает («81 / 30»). */}
                    <span className={cn('font-bold tabular', daysTextColor)}>{daysLeftLabel}</span>
                  </div>
                  <ProgressBar value={progressRatio / 100} tone="green" />
                </div>
              )}

              <div>
                <div className="flex justify-between text-sm mb-2.5">
                  {/* Показываем остаток, а не расход: счётчик AdaptGroup накопительный за
                      всю жизнь подписки, и «19.2 из 90» рядом со счётчиком дней читалось
                      как месячная квота, которая «не обновилась» после продления. */}
                  <span className="text-[#8a8c93] font-medium">{t('Traffic bypass left')}</span>
                  <span className="font-bold tabular text-foreground">
                    {sub.trafficTotalGb === null
                      ? '∞'
                      : Math.max(0, sub.trafficTotalGb - sub.trafficUsedGb).toFixed(1)}{' '}
                    <span className="text-[#8a8c93] font-medium">
                      {sub.trafficTotalGb === null ? '' : t('Traffic of total', { total: sub.trafficTotalGb })}
                    </span>
                  </span>
                </div>
                <ProgressBar value={trafficPct / 100} tone="blue" />
                <div className="text-[11px] text-[#6b6d74] mt-1.5 leading-snug">
                  {lifetimeSection
                    ? t('Traffic note lifetime')
                    : sub.isTrial
                      ? t('Traffic note trial')
                      : t('Traffic note paid', { used: sub.trafficUsedGb.toFixed(1) })}
                </div>
              </div>
            </div>

            {/* Автопродление только у платных подписок на срок: «Навсегда» не продлевается,
                пробник тоже, и бэк включить его пробнику не даст (`trial_cannot_renew`).
                Тумблер раньше был и у пробника: флаг вставал, а воркер раз в час падал на
                попытке продлить. */}
            {!lifetimeSection && !sub.isTrial && (
              <div className="flex items-center gap-3 rounded-2xl p-3.5" style={{ background: 'rgba(255,255,255,0.035)' }}>
                <IconChip icon={RefreshCw} tone="blue" size={40} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground">{t('Auto-renew')}</div>
                  <div className="text-xs text-[#8a8c93] mt-0.5 leading-snug">{t('Auto-renew desc')}</div>
                </div>
                <Switch checked={autoRenew} aria-label={t('Auto-renew')} onCheckedChange={toggleAutoRenew} />
              </div>
            )}

            {autoRenew && !lifetimeSection && !sub.isTrial && (
              <div className="flex flex-wrap items-center gap-2 px-1">
                <span className="text-xs text-[#8a8c93]">{t('Auto renew period')}</span>
                <button
                  type="button"
                  onClick={() => setAutoRenewDays(null)}
                  className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                    autoRenewDays == null
                      ? 'bg-accent/20 text-accent'
                      : 'bg-white/[0.06] text-[#8a8c93]'
                  }`}
                >
                  {t('Auto renew period plan')}
                </button>
                {/* Срок мог прийти не из этого списка (выбран в боте): тогда его плашка
                    тоже нужна, иначе не выбрано ничего и непонятно, что сейчас стоит. */}
                {(autoRenewDays != null && ![7, 14, 30].includes(autoRenewDays)
                  ? [7, 14, 30, autoRenewDays].sort((a, b) => a - b)
                  : [7, 14, 30]
                ).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setAutoRenewDays(d)}
                    className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                      autoRenewDays === d
                        ? 'bg-accent/20 text-accent'
                        : 'bg-white/[0.06] text-[#8a8c93]'
                    }`}
                  >
                    {t('Auto renew period custom', { count: d })}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Primary CTA — подключить VPN. configUrl уже есть после успешной покупки. */}
        {sub.configUrl && (
          <button
            type="button"
            onClick={() => navigate({ to: '/success/$subscriptionId', params: { subscriptionId: sub.id } })}
            className="w-full calm-cta py-4 rounded-2xl text-base font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          >
            <Zap className="w-5 h-5" fill="currentColor" />
            {t('Connect VPN')}
          </button>
        )}

        {/* Сетка действий. У «Навсегда» нет продления, апгрейда и заморозки: вместо них
            докупки трафика обхода и +15 устройств. */}
        {lifetimeSection ? (
          <div className="grid grid-cols-3 gap-2">
            {sub.isTrial ? (
              <ActionTile
                icon={Plus}
                label={t('Choose plan')}
                onClick={() => navigate({ to: '/plans', search: { section: 'lifetime' } })}
              />
            ) : (
              <>
                <ActionTile
                  icon={PlusSquare}
                  label={t('Buy GB')}
                  onClick={() => setAddonKind('traffic')}
                  className={sub.addonsAvailable ? undefined : 'opacity-50'}
                />
                <ActionTile
                  icon={Smartphone}
                  label={t('Add devices')}
                  onClick={() => setAddonKind('devices')}
                  className={sub.addonsAvailable ? undefined : 'opacity-50'}
                />
              </>
            )}
            <ActionTile icon={Gift} label={t('Gift')} onClick={() => navigate({ to: '/gift' })} />
            <ActionTile icon={Wallet} label={t('Top up')} onClick={() => navigate({ to: '/topup' })} />
          </div>
        ) : (
        <div className="grid grid-cols-3 gap-2">
          <ActionTile
            icon={Plus}
            label={t('Renew')}
            onClick={() => {
              // Пробник не продлевается: ведём в тарифы. Раньше вели на апгрейд, а там
              // для трёх дней вариантов нет и советовали дождаться конца пробника.
              if (sub.isTrial) {
                toast.info(t('Trial cannot renew'));
                navigate({ to: '/plans' });
                return;
              }
              navigate({ to: '/renew/$subscriptionId', params: { subscriptionId: sub.id } });
            }}
          />
          <ActionTile icon={ArrowUp} label={t('Upgrade action')} onClick={() => navigate({ to: '/upgrade' })} />
          <ActionTile
            icon={Snowflake}
            label={isFrozen ? t('Unfreeze') : t('Freeze')}
            onClick={openFreeze}
            className={freezeBlocked ? 'opacity-50' : undefined}
          />
          <ActionTile icon={PlusSquare} label={t('Buy GB')} onClick={() => setTrafficOpen(true)} />
          <ActionTile icon={Gift} label={t('Gift')} onClick={() => navigate({ to: '/gift' })} />
          <ActionTile icon={Wallet} label={t('Top up')} onClick={() => navigate({ to: '/topup' })} />
        </div>
        )}

        <MirrorTile />

        {/* Вторая подписка это отдельный вход в /plans, а не «продлить»: с карточки
            подписки его не было, и человек искал «купить ещё» в продлении (тикет #24). */}
        <button
          type="button"
          onClick={() => {
            haptic('light');
            navigate({ to: '/plans' });
          }}
          className="w-full rounded-2xl bg-white/[0.05] px-4 py-3.5 text-left flex items-center justify-between gap-3 active:scale-[0.98] transition-transform"
        >
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground truncate">{t('Buy another plan')}</span>
            <span className="block text-xs text-[#8a8c93] line-clamp-2">{t('Buy another plan hint')}</span>
          </span>
          <ChevronRight className="w-4 h-4 text-[#8a8c93] shrink-0" />
        </button>

        {customSubButtons.length > 0 && (
          <div className="space-y-2">
            {customSubButtons.map((b, i) => (
              <button
                key={`${b.text}-${i}`}
                type="button"
                onClick={() => {
                  haptic('light');
                  if (b.path) navigate({ to: b.path as '/' });
                  else if (b.url) openInvoiceUrl(b.url);
                }}
                className="w-full rounded-2xl bg-white/[0.05] px-4 py-3.5 text-sm font-semibold text-foreground text-left flex items-center justify-between active:scale-[0.98] transition-transform"
              >
                <span className="truncate">{b.text}</span>
                <ChevronRight className="w-4 h-4 text-[#8a8c93] shrink-0" />
              </button>
            ))}
          </div>
        )}

        {/* Устройства. С 13.10.2026 список есть и у «Навсегда»: поставщик его отдаёт,
            но на главной у неё вместо превью лимит (см. запрос `devices` выше). */}
        <div className="calm-card rounded-[24px] p-5 flex flex-col gap-4">
          {/* Кликабельна вся шапка карточки, а не только «Управлять ›» — иначе тап по
              крупному заголовку/телу ничего не делал и воспринимался как «кнопка не работает». */}
          <Link
            to="/devices"
            className="flex justify-between items-center -m-1 p-1 rounded-xl transition-opacity active:opacity-70"
          >
            <h3 className="font-bold text-foreground">{t('Devices')}</h3>
            <span className="text-accent text-sm font-semibold flex items-center gap-1">
              {t('Manage')} <ChevronRight className="w-4 h-4" />
            </span>
          </Link>

          {lifetimeSection ? (
            <p className="text-sm text-[#8a8c93] leading-snug">
              {t('Lifetime devices', { count: sub.devices })}
            </p>
          ) : (
          <div className="space-y-2">
            {(devices ?? []).slice(0, 2).map((dev) => (
              <div key={dev.id} className="flex items-center gap-3 p-3 rounded-[15px]" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <IconChip icon={dev.os === 'iOS' || dev.os === 'Android' ? Smartphone : Laptop} tone="blue" size={40} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium line-clamp-2 [overflow-wrap:anywhere] text-foreground">{dev.name}</div>
                  <div className="text-[11px] text-[#5fe3ad] truncate flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#38e0a5]" style={{ boxShadow: '0 0 6px #38e0a5' }} />
                    {t('Connected')}{dev.geo ? ` • ${dev.geo}` : ''}
                  </div>
                </div>
              </div>
            ))}
            {devices && devices.length === 0 && (
              <div className="text-sm text-[#8a8c93] text-center py-4">{t('No devices yet')}</div>
            )}
          </div>
          )}

          <RotateLinkRow sub={sub} />
        </div>

        <LocationsTile section={lifetimeSection ? 'lifetime' : 'term'} />
        <CountriesRow subscriptionId={sub.id} />

        {/* Пригласи друга — синий tint. Скрыт если партнёр выключил реф-программу. */}
        {referralEnabled && (
        <div ref={referralRef} className="rounded-[24px] p-6 flex flex-col justify-center relative overflow-hidden border border-primary/25" style={{ background: 'linear-gradient(168deg, rgb(var(--brand-primary-rgb) / 0.16), rgb(var(--brand-primary-rgb) / 0.04))' }}>
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary blur-[60px] opacity-25 pointer-events-none" />
          <div className="relative z-10 space-y-3">
            <div className="flex items-center gap-3">
              <IconChip icon={Users} tone="blue" size={42} />
              <h4 className="text-lg font-bold text-foreground">{t('Invite a friend')}</h4>
            </div>
            <p className="text-sm text-[#c2c4ca] leading-snug">
              {t('Referral explain l1')}{' '}
              <b className="text-foreground">{refL1Percent}%</b> {t('Referral explain l1 tail')}
              {refL2Percent > 0 && (
                <>
                  {' '}{t('Referral explain l2')}{' '}
                  <b className="text-foreground">{refL2Percent}%</b> {t('Referral explain l2 tail')}
                </>
              )}
            </p>
            {refInvited > 0 && (
              <p className="text-xs text-[#8a8c93]">
                {t('Already invited')} <b className="text-foreground">{refInvited}</b>
              </p>
            )}
            <ReferralWithdrawBlock />
            <button
              type="button"
              onClick={copyReferral}
              disabled={!referralLink}
              className="flex items-center justify-center gap-2 w-full calm-cta font-bold py-3 rounded-xl text-sm active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              <Copy className="w-4 h-4" />
              {t('Copy Link')}
            </button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={freezeOpen}
        onClose={() => setFreezeOpen(false)}
        onConfirm={() => freeze()}
        title={isFrozen ? t('Unfreeze title') : t('Freeze title')}
        description={
          isFrozen
            ? t('Unfreeze desc')
            : t('Freeze desc')
        }
        confirmLabel={freezing ? '…' : isFrozen ? t('Unfreeze') : t('Freeze')}
        destructive={!isFrozen}
      />

      <TrafficSheet open={trafficOpen} onClose={() => setTrafficOpen(false)} subscriptionId={sub.id} />
      <LifetimeAddonSheet
        kind={addonKind}
        onClose={() => setAddonKind(null)}
        sub={sub}
      />
    </>
  );
}

/**
 * Вывод реферального заработка в карточке «Пригласи друга» (идея #162).
 *
 * Блок есть, только если партнёр включил вывод (или у клиента висит заявка, которую
 * можно отменить). Пока ответ едет, на месте сумм скелетон: «Можно вывести 0 ₽» до
 * загрузки читалось бы как факт. Упал запрос — блока нет: про вывод, который партнёр
 * мог и не включать, ошибку не показываем, 5xx и так ловит корневой тост.
 */
function ReferralWithdrawBlock() {
  const { t, i18n } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: WITHDRAW_QUERY_KEY,
    queryFn: () => api.referralWithdrawState(),
    staleTime: 30_000,
  });

  if (isPending) return <ReferralWithdrawBlockSkeleton />;
  if (!data || (!data.enabled && !data.open)) return null;

  const money = (minor: number) => formatWholeMinor(minor, data.currency, i18n.language);
  const canWithdraw = data.enabled && data.available_display_minor >= data.min_display_minor;
  // Владелец сервиса нажал «Перевожу»: заявка уже не «ждёт», и отменить её нельзя.
  const sending = data.open?.status === 'processing';

  return (
    <div className="rounded-2xl bg-white/[0.05] p-3.5 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0 flex flex-col justify-between gap-0.5">
          <div className="text-[11px] text-[#8a8c93]">{t('Withdraw earned')}</div>
          <div className="font-bold tabular text-foreground truncate">{money(data.earned_display_minor)}</div>
        </div>
        <div className="min-w-0 flex flex-col justify-between gap-0.5">
          <div className="text-[11px] text-[#8a8c93]">{t('Withdraw available')}</div>
          <div className="font-bold tabular text-[#5fe3ad] truncate">{money(data.available_display_minor)}</div>
        </div>
      </div>
      {data.open ? (
        <Link
          to="/referral-withdraw"
          className={cn(
            'flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-[13px] font-semibold active:opacity-70 transition-opacity',
            sending ? 'bg-[rgba(90,160,255,0.1)] text-[#5aa0ff]' : 'bg-[rgba(240,176,78,0.1)] text-[#f0b04e]',
          )}
        >
          <span className="flex items-center gap-2 min-w-0">
            {sending ? <Send className="w-4 h-4 shrink-0" /> : <Clock className="w-4 h-4 shrink-0" />}
            <span className="leading-snug">
              {sending
                ? t('Withdraw processing short', { amount: money(data.open.amount_display_minor) })
                : t('Withdraw open short', { amount: money(data.open.amount_display_minor) })}
            </span>
          </span>
          <ChevronRight className="w-4 h-4 shrink-0" />
        </Link>
      ) : canWithdraw ? (
        <Link
          to="/referral-withdraw"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/[0.08] py-2.5 text-sm font-bold text-foreground active:scale-[0.98] transition-transform"
        >
          <Wallet className="w-4 h-4" />
          {t('Withdraw submit')}
        </Link>
      ) : (
        // Не хватает до минимума: большая кнопка «Вывести» вела бы в отказ. Экран
        // вывода всё равно доступен, там объяснение и история заявок.
        <Link
          to="/referral-withdraw"
          className="flex items-center justify-between gap-2 text-[12px] text-[#8a8c93] active:opacity-70 transition-opacity"
        >
          <span>{t('Withdraw from min', { amount: money(data.min_display_minor) })}</span>
          <ChevronRight className="w-4 h-4 shrink-0" />
        </Link>
      )}
    </div>
  );
}

/** Повторяет плашку сумм: две колонки «подпись над числом». */
function ReferralWithdrawBlockSkeleton() {
  return (
    <div className="rounded-2xl bg-white/[0.05] p-3.5 grid grid-cols-2 gap-3">
      {[0, 1].map((i) => (
        <div key={i} className="space-y-1.5">
          <SkeletonLine size="xs" className="w-2/3" />
          <SkeletonLine size="lg" className="w-1/2" />
        </div>
      ))}
    </div>
  );
}

/** Подтверждающий тап раньше этого срока считаем вторым щелчком двойного клика. */
const ROTATE_CONFIRM_DELAY_MS = 600;

/**
 * «Обновить ссылку» — отзыв публичного адреса подписки.
 *
 * Механика и текст те же, что у кнопки в боте. Разница была в доступности: в боте
 * она лежит прямо в меню подписки, а в мини-аппе была закопана внутри свёрнутого
 * блока на экране успешной покупки — до неё доходили три тапа и скролл. Здесь она
 * стоит в карточке устройств, где человек и задаётся вопросом «кто ещё подключён».
 *
 * Кулдаун проверяем ДО нажатия (бэк отдаёт `linkRotationAvailableAt`): раньше про
 * него узнавали только по ошибке, уже нажав.
 */
function RotateLinkRow({ sub }: { sub: Subscription }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Когда показали «Нажмите ещё раз». Второй щелчок двойного клика приходил через
  // доли секунды и сам становился подтверждением: ссылка менялась, хотя человек
  // предупреждение ещё не прочёл.
  const armedAt = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => () => {
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
  }, []);

  const availableAt = sub.linkRotationAvailableAt
    ? new Date(sub.linkRotationAvailableAt)
    : null;
  const waitMinutes =
    availableAt && availableAt.getTime() > Date.now()
      ? Math.ceil((availableAt.getTime() - Date.now()) / 60000)
      : 0;

  const rotate = useMutation({
    mutationFn: () => api.rotateSubscriptionLink(sub.id),
    onSettled: () => {
      busyRef.current = false;
    },
    onSuccess: (resp) => {
      setConfirming(false);
      if (!resp?.subscription_url) {
        haptic('error');
        toast.error(t('Rotate failed'));
        return;
      }
      haptic('success');
      setNewUrl(resp.subscription_url);
      void qc.invalidateQueries({ queryKey: ['subs'] });
    },
    onError: () => {
      setConfirming(false);
      haptic('error');
      toast.error(t('Rotate failed'));
    },
  });

  const onClick = () => {
    if (waitMinutes > 0 || rotate.isPending || busyRef.current) return;
    if (!confirming) {
      // Двухшаговое подтверждение вместо попапа: действие необратимое, но не
      // разрушительное, а лишний системный диалог на этом экране только мешает.
      setConfirming(true);
      armedAt.current = Date.now();
      confirmTimer.current = setTimeout(() => setConfirming(false), 5000);
      return;
    }
    if (Date.now() - armedAt.current < ROTATE_CONFIRM_DELAY_MS) return;
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    busyRef.current = true;
    rotate.mutate();
  };

  return (
    <div className="border-t border-white/5 pt-4 space-y-2">
      <button
        type="button"
        onClick={onClick}
        disabled={waitMinutes > 0 || rotate.isPending}
        className="w-full flex items-center gap-3 rounded-2xl p-3 text-left transition-opacity active:opacity-70 disabled:opacity-45"
        style={{ background: 'rgba(255,255,255,0.035)' }}
      >
        <IconChip icon={RefreshCw} tone="blue" size={40} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            {rotate.isPending
              ? t('Rotating')
              : confirming
                ? t('Rotate confirm')
                : t('Rotate link')}
          </div>
          <div className="text-xs text-[#8a8c93] mt-0.5">
            {waitMinutes > 0
              ? t('Rotate wait', { count: waitMinutes })
              : t('Rotate warn short')}
          </div>
        </div>
      </button>
      {newUrl ? (
        <div className="rounded-2xl p-3 space-y-1" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <div className="text-xs text-[#8a8c93]">{t('New sub address')}</div>
          <div className="text-[11px] font-mono break-all text-foreground">{newUrl}</div>
        </div>
      ) : (
        <p className="text-[11px] text-[#8a8c93] leading-snug px-1">
          {t('Rotate note')}
        </p>
      )}
    </div>
  );
}

/**
 * Вход в выбор стран подписки (заявка #169). Нет, пока не знаем, разрешил ли его
 * владелец сервиса: строка, ведущая в запертый экран, только путала бы.
 */
function CountriesRow({ subscriptionId }: { subscriptionId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['sub-countries', subscriptionId],
    queryFn: () => api.subscriptionCountries(subscriptionId),
    staleTime: 60 * 1000,
  });
  if (!data?.enabled || data.countries.length < 2) return null;
  const total = data.countries.length;
  const shown = total - data.hidden.length;
  return (
    <Link
      to="/countries/$subscriptionId"
      params={{ subscriptionId }}
      preload="intent"
      className="flex items-center gap-3 calm-card rounded-[24px] p-4 active:scale-[0.99] transition-transform"
    >
      <IconChip icon={ListChecks} tone="blue" size={40} />
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm text-foreground">{t('Countries in subscription')}</div>
        <div className="text-[11px] text-[#8a8c93] tabular">
          {data.hidden.length ? t('Countries shown of', { shown, total }) : t('All countries shown')}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#8a8c93] shrink-0" />
    </Link>
  );
}

function LocationsTile({ section = 'term' }: { section?: 'term' | 'lifetime' }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['client-locations', section],
    queryFn: () => api.locations(section),
    staleTime: 5 * 60 * 1000,
  });

  const total = data?.total ?? 0;
  if (total === 0) return null;

  const preview = Object.values(data?.regions ?? {})
    .flat()
    .slice(0, 8);

  return (
    <Link
      to="/locations"
      // Плитка считает серверы раздела подписки, и список обязан открыться тот же:
      // без `section` у подписчика «Навсегда» открывались серверы «На срок».
      search={{ section }}
      preload="intent"
      className="block calm-card rounded-[24px] p-5 relative overflow-hidden active:scale-[0.99] transition-transform"
    >
      <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-primary blur-[70px] opacity-[0.12] pointer-events-none" />
      <div className="relative z-10 flex items-center gap-3">
        <IconChip icon={Globe2} tone="blue" size={44} />
        <div className="min-w-0 flex-1">
          <SectionLabel className="mb-0.5">{t('Servers')}</SectionLabel>
          <div className="font-bold text-base text-foreground">
            {total} <span className="text-[#8a8c93] font-medium">{t('Locations word', { count: total })}</span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#8a8c93] shrink-0" />
      </div>
      <div className="relative z-10 mt-3 flex flex-wrap gap-1.5">
        {preview.map((loc, i) => (
          <span
            key={`${loc.flag}-${i}`}
            className="text-lg leading-none inline-flex items-center justify-center w-8 h-8 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.04)' }}
          >
            <Flag flag={loc.flag} code={loc.country_code} />
          </span>
        ))}
        {total > preview.length && (
          <span className="text-[11px] inline-flex items-center px-2.5 h-8 rounded-lg text-[#8a8c93] font-semibold" style={{ background: 'rgba(255,255,255,0.04)' }}>
            +{total - preview.length}
          </span>
        )}
      </div>
    </Link>
  );
}


/**
 * Повторяет карточку тарифа поблочно: заголовок со статусом, две полосы
 * (дни и трафик), строка автопродления. Глухая плита на 260px, которая тут
 * стояла раньше, ничего не обещала — экран всё равно перекладывался целиком.
 */
function DashboardSkeleton() {
  return (
    <SkeletonPage className="space-y-5">
      <SkeletonCard className="rounded-[24px] p-5 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-2.5">
            <SkeletonLine size="xs" className="w-24" />
            <SkeletonLine size="hero" className="w-2/3" />
          </div>
          <Skeleton className="h-[26px] w-20 rounded-full shrink-0" />
        </div>
        {[0, 1].map((i) => (
          <div key={i} className="space-y-2.5">
            <div className="flex justify-between">
              <SkeletonLine size="sm" className="w-28" />
              <SkeletonLine size="sm" className="w-16" />
            </div>
            <Skeleton className="h-[7px] w-full rounded-full" />
          </div>
        ))}
        <div className="flex items-center gap-3 rounded-2xl p-3.5" style={{ background: 'rgba(255,255,255,0.035)' }}>
          <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <SkeletonLine className="w-1/3" />
            <SkeletonLine size="xs" className="w-1/2" />
          </div>
          <Skeleton className="h-[26px] w-11 rounded-full shrink-0" />
        </div>
      </SkeletonCard>
      <Skeleton className="h-[58px] w-full rounded-2xl" />
      <SkeletonGrid count={6} cols={3} className="gap-2" itemClassName="h-[88px] min-h-0 rounded-2xl" />
      <SkeletonCard className="h-[140px] rounded-[24px]" />
      <SkeletonCard className="h-[120px] rounded-[24px]" />
    </SkeletonPage>
  );
}

/**
 * Вход в розыгрыши. Шестая вкладка в таббар не влезает (пятая уже режется на узких
 * экранах), поэтому карточкой на главной — и первым блоком, над карточкой тарифа.
 * Внизу, под устройствами и локациями, её не находили: до неё нужно было проскроллить
 * весь экран, а розыгрыш живёт считанные дни.
 *
 * Скрыта, когда розыгрышей нет: пустая карточка «сейчас ничего» на главном экране
 * только занимает место. Показывается всем клиентам бота, включая тех, у кого нет
 * активной подписки: розыгрыш — это как раз способ их вернуть.
 */
function GiveawayTile() {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['giveaways'],
    queryFn: () => api.giveaways(),
    staleTime: 60_000,
  });
  const list = data ?? [];
  if (list.length === 0) return null;

  const joined = list.filter((g) => g.joined).length;
  const ready = list.filter((g) => g.can_join).length;
  const hint =
    ready > 0
      ? t('Giveaway tile ready')
      : joined === list.length
        ? t('Giveaway tile joined')
        : t('Giveaway tile todo');

  return (
    <Link
      to="/giveaways"
      className="rounded-[24px] p-5 flex items-center gap-3 relative overflow-hidden border border-primary/25 active:scale-[0.99] transition-transform"
      style={{ background: 'linear-gradient(168deg, rgb(var(--brand-primary-rgb) / 0.16), rgb(var(--brand-primary-rgb) / 0.04))' }}
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-primary blur-[50px] opacity-25 pointer-events-none" />
      <IconChip icon={Gift} tone="blue" size={42} className="relative z-10" />
      <div className="flex-1 min-w-0 relative z-10">
        <div className="font-bold text-foreground">
          {t('Giveaways')}{list.length > 1 ? ` · ${list.length}` : ''}
        </div>
        <div className="text-[12px] text-[#8a8c93] line-clamp-2">{hint}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#8a8c93] relative z-10 shrink-0" />
    </Link>
  );
}

/**
 * «🏪 Свой VPN»: клиент заводит собственный магазин поверх этого бота (заявка #96).
 * Само создание живёт в чате (токен или мгновенное создание), поэтому карточка
 * закрывает мини-апп и открывает тот же экран в боте диплинком `open_mirror`.
 * Видна только при включённом у бота тумблере, как и кнопка в меню бота.
 */
function MirrorTile() {
  const { t } = useTranslation();
  const { data: info } = useQuery({ queryKey: ['info'], queryFn: () => api.info(), staleTime: 300_000 });
  if (!api.getAuth().mirrorsEnabled || !info?.bot_username) return null;
  const open = () => {
    haptic('light');
    const tg = getWebApp();
    const url = `https://t.me/${info.bot_username}?start=open_mirror`;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
    tg?.close();
  };
  return (
    <button
      type="button"
      onClick={open}
      className="w-full rounded-[24px] p-5 flex items-center gap-3 relative overflow-hidden border border-primary/25 text-left active:scale-[0.99] transition-transform"
      style={{ background: 'linear-gradient(168deg, rgb(var(--brand-primary-rgb) / 0.16), rgb(var(--brand-primary-rgb) / 0.04))' }}
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-primary blur-[50px] opacity-25 pointer-events-none" />
      <IconChip icon={Store} tone="blue" size={42} className="relative z-10" />
      <div className="flex-1 min-w-0 relative z-10">
        <div className="font-bold text-foreground">{t('Own VPN')}</div>
        <div className="text-[12px] text-[#8a8c93] line-clamp-2">{t('Own VPN hint')}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#8a8c93] relative z-10 shrink-0" />
    </button>
  );
}

function EmptyHome({
  onPickPlan,
  expired,
}: {
  onPickPlan: () => void;
  /** Последняя подписка, истёкшая за 30 дней: её продлевают, а не покупают заново. */
  expired: (Subscription & { renewable: boolean }) | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="p-4 pb-24 space-y-5">
      {expired && <ExpiredCard sub={expired} />}
      <div className="relative overflow-hidden rounded-[28px] calm-card-hero p-7">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-primary blur-[90px] opacity-20 pointer-events-none" />
        <div className="relative z-10 space-y-4">
          <IconChip icon={Shield} tone="blue" size={56} radius={16} />
          <div>
            <h1 className="text-2xl font-bold leading-tight mb-1.5 text-foreground">{t('Home hero title')}</h1>
            <p className="text-sm text-[#c2c4ca]">
              {t('Home hero desc')}
            </p>
          </div>
          <button
            type="button"
            onClick={onPickPlan}
            className="w-full calm-cta h-12 text-base rounded-2xl font-bold active:scale-[0.98] transition-transform"
          >
            {t('Open plans')}
          </button>
        </div>
      </div>

      <GiveawayTile />
      <MirrorTile />

      <div className="grid grid-cols-3 gap-2">
        <FeatureChip icon={Zap} title={t('Feature fast')} hint={t('Feature fast hint')} />
        <FeatureChip icon={Shield} title={t('Feature private')} hint={t('Feature private hint')} />
        <FeatureChip icon={Sparkles} title={t('Feature simple')} hint={t('Feature simple hint')} />
      </div>

      <div className="rounded-[24px] calm-card p-5 space-y-3">
        <h3 className="font-semibold text-foreground">{t('Have promo')}</h3>
        <p className="text-sm text-[#8a8c93]">{t('Have promo desc')}</p>
      </div>
    </div>
  );
}

/**
 * Подписка закончилась, но её можно продлить с той же ссылкой: настраивать
 * устройства заново не нужно. Если тариф сняли с продажи или это спецтариф, продлить
 * нельзя, и кнопка ведёт в выбор тарифа.
 */
function ExpiredCard({ sub }: { sub: Subscription & { renewable: boolean } }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="rounded-[24px] calm-card p-5 space-y-3">
      <div className="flex items-center gap-3">
        <IconChip icon={CalendarClock} tone="amber" size={42} />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[#8a8c93]">{t('Expired title')}</div>
          <div className="font-extrabold text-foreground line-clamp-2">{sub.planName}</div>
          <div className="text-sm text-[#f0b04e]">
            {t('Expired on', { date: shortDate(sub.expiresAt, i18n.language) })}
          </div>
        </div>
      </div>
      <p className="text-sm text-[#c2c4ca]">
        {sub.renewable ? t('Expired renew hint') : t('Expired gone hint')}
      </p>
      <button
        type="button"
        onClick={() =>
          sub.renewable
            ? navigate({ to: '/renew/$subscriptionId', params: { subscriptionId: sub.id } })
            : navigate({ to: '/plans' })
        }
        className="w-full calm-cta h-12 text-base rounded-2xl font-bold active:scale-[0.98] transition-transform"
      >
        {sub.renewable ? t('Renew') : t('Go to paid plan')}
      </button>
    </div>
  );
}

function FeatureChip({ icon, title, hint }: { icon: typeof Zap; title: string; hint: string }) {
  return (
    <div className="rounded-2xl calm-card p-3 flex flex-col gap-1.5">
      <IconChip icon={icon} tone="blue" size={32} radius={10} />
      <div className="text-xs font-bold text-foreground">{title}</div>
      <div className="text-[10px] text-[#8a8c93] leading-tight">{hint}</div>
    </div>
  );
}

function TrafficSheet({ open, onClose, subscriptionId }: { open: boolean; onClose: () => void; subscriptionId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number | null>(null);
  // Пусто до ответа, дальше первый способ сервера (СБП), а не CryptoBot.
  const [provider, setProvider] = useState<string>('');

  const { data: packs } = useQuery({
    queryKey: ['traffic-packs', subscriptionId],
    queryFn: () => (subscriptionId ? api.trafficPacks(subscriptionId) : Promise.resolve({ packs: [] })),
    enabled: !!subscriptionId,
  });
  const { data: providersData } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => api.paymentProviders(),
  });

  useEffect(() => {
    if (providersData?.providers && providersData.providers.length > 0 && !providersData.providers.includes(provider)) {
      setProvider(providersData.providers[0]);
    }
  }, [providersData, provider]);

  const { mutate: buyPack, isPending } = useMutation({
    mutationFn: (gb: number) => {
      if (!subscriptionId) throw new Error('no subscription');
      return api.createTrafficIntent(subscriptionId, { gb_amount: gb, provider });
    },
    onSuccess: (intent) => {
      openInvoiceUrl(intent.invoice_url);
      haptic('success');
      toast.success(t('Traffic invoice open'));
      onClose();
      navigate({ to: '/processing/$paymentId', params: { paymentId: intent.payment_id } });
    },
    onError: (e: Error) => {
      toast.error(t('Invoice failed detail', { error: e.message }));
    },
  });

  const selectedPack = packs?.packs.find((p) => p.gb === selected);
  const priceLabel = (cost: number, currency: string) =>
    currency === 'RUB' ? rub(Math.floor(cost / 100)) : `${groupDigits(Math.floor(cost / 100))} ${currency}`;

  return (
    <BottomSheet open={open} onClose={onClose} title={t('Buy traffic')}>
      <p className="text-sm text-[#8a8c93]">
        {t('Buy traffic desc')}
      </p>
      <div className="space-y-2">
        {(packs?.packs ?? []).map((p) => (
          <button
            key={p.gb}
            type="button"
            onClick={() => setSelected(p.gb)}
            className={cn(
              'w-full text-left p-4 rounded-2xl border transition-all flex items-center justify-between active:scale-[0.98]',
              selected === p.gb ? 'border-accent bg-primary/8' : 'calm-card',
            )}
          >
            <div className="font-bold text-foreground">
              {p.gb >= 9000 ? t('GB pack unlimited') : t('GB pack', { gb: p.gb })}
            </div>
            <div className="font-bold tabular text-foreground">
              {priceLabel(p.total_display || p.cost_display, p.currency)}
            </div>
          </button>
        ))}
      </div>
      <ProviderPills providers={providersData?.providers ?? []} value={provider} onChange={setProvider} />
      <button
        type="button"
        disabled={!selected || !provider || isPending}
        onClick={() => selected && provider && buyPack(selected)}
        className="w-full calm-cta py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        {selectedPack
          ? t('Buy for', {
              price: priceLabel(
                selectedPack.total_display || selectedPack.cost_display,
                selectedPack.currency,
              ),
            })
          : t('Pick a pack')}
      </button>
    </BottomSheet>
  );
}

/**
 * Докупка к «Навсегда»: пакет трафика обхода или +15 устройств.
 *
 * Два способа, как у тарифа: счётом (выбранный способ оплаты) и с баланса, если его
 * хватает. С баланса докупка применяется сразу, счётом после оплаты (экран
 * `processing`, как у остальных счетов).
 */
function LifetimeAddonSheet({
  kind,
  onClose,
  sub,
}: {
  kind: 'traffic' | 'devices' | null;
  onClose: () => void;
  sub: Subscription;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [provider, setProvider] = useState<string>('');
  // Ключ намерения на одно открытие шторки: двойной тап и повтор после сбоя
  // поставщика не покупают второй раз. После успеха шторка закрывается, а следующее
  // открытие берёт новый ключ.
  const [nonce, setNonce] = useState(newIntentNonce);
  const open = kind !== null;

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['lifetime-addons', sub.id],
    queryFn: () => api.lifetimeAddons(sub.id),
    enabled: open && Boolean(sub.addonsAvailable),
  });
  const { data: providersData } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => api.paymentProviders(),
    enabled: open,
  });

  useEffect(() => {
    if (open) setNonce(newIntentNonce());
    else setSelectedKey(null);
  }, [open]);
  useEffect(() => {
    const list = providersData?.providers ?? [];
    if (list.length > 0 && !list.includes(provider)) setProvider(list[0]);
  }, [providersData, provider]);

  const offers = (data?.offers ?? []).filter((o) => o.kind === kind);
  useEffect(() => {
    // Устройства продаются одним пакетом: выбирать там нечего.
    if (kind === 'devices' && offers.length === 1 && selectedKey === null) {
      setSelectedKey(offers[0].key);
    }
  }, [kind, offers, selectedKey]);
  const selected = offers.find((o) => o.key === selectedKey) ?? null;
  const balanceEnough = selected !== null && (data?.balance_usd_minor ?? 0) >= selected.cost_usd_minor;
  const rubMinor = (minor: number) => rub(Math.floor(minor / 100));

  const invoice = useMutation({
    mutationFn: () => {
      if (!selected || !kind) throw new Error('no add-on');
      return api.createAddonIntent(sub.id, { kind, key: selected.key, provider, nonce });
    },
    onSuccess: (intent) => {
      openInvoiceUrl(intent.invoice_url);
      haptic('success');
      onClose();
      navigate({ to: '/processing/$paymentId', params: { paymentId: intent.payment_id } });
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(t('Invoice failed detail', { error: e.message }));
    },
  });
  const fromBalance = useMutation({
    mutationFn: () => {
      if (!selected || !kind) throw new Error('no add-on');
      return api.buyAddonFromBalance(sub.id, { kind, key: selected.key, nonce });
    },
    onSuccess: (res) => {
      haptic('success');
      toast.success(
        kind === 'devices'
          ? t('Addon done devices', { count: res.devices })
          : t('Addon done traffic', { gb: selected?.gb ?? 0 }),
      );
      qc.invalidateQueries({ queryKey: ['subs'] });
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['lifetime-addons', sub.id] });
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      const code = e instanceof ApiError ? e.code : undefined;
      toast.error(
        code === 'app.subscriptions.insufficient_balance'
          ? t('Addon no balance')
          : isProviderError(e)
            ? t('Provider unavailable')
            : e.message,
      );
    },
  });
  const busy = invoice.isPending || fromBalance.isPending;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={kind === 'devices' ? t('Add devices title') : t('Buy traffic')}
    >
      <p className="text-sm text-[#8a8c93]">
        {kind === 'devices'
          ? t('Add devices desc', { now: sub.devices, after: sub.devices + 15 })
          : t('Lifetime traffic desc')}
      </p>
      {!sub.addonsAvailable ? (
        <div className="rounded-2xl calm-card p-4 text-sm text-[#8a8c93]">{t('Addons unavailable')}</div>
      ) : isPending ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-[60px] rounded-2xl" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-2xl calm-card p-4 text-sm text-[#8a8c93] space-y-2">
          <div>{t('Addons load failed')}</div>
          <button type="button" onClick={() => refetch()} className="font-semibold text-accent min-h-[44px]">
            {t('Retry')}
          </button>
        </div>
      ) : offers.length === 0 ? (
        <div className="rounded-2xl calm-card p-4 text-sm text-[#8a8c93]">{t('Addons unavailable')}</div>
      ) : (
        <>
          <div className="space-y-2">
            {offers.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setSelectedKey(o.key)}
                className={cn(
                  'w-full text-left p-4 rounded-2xl border transition-all flex items-center justify-between active:scale-[0.98]',
                  selectedKey === o.key ? 'border-accent bg-primary/8' : 'calm-card',
                )}
              >
                <div className="font-bold text-foreground">
                  {o.kind === 'devices'
                    ? t('Addon devices pack', { count: o.devices ?? 15 })
                    : t('GB pack', { gb: o.gb ?? 0 })}
                </div>
                <div className="font-bold tabular text-foreground">{rubMinor(o.total_display || o.cost_display)}</div>
              </button>
            ))}
          </div>
          <ProviderPills providers={providersData?.providers ?? []} value={provider} onChange={setProvider} />
          <button
            type="button"
            disabled={!selected || !provider || busy}
            onClick={() => invoice.mutate()}
            className="w-full calm-cta py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {invoice.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {selected
              ? t('Buy for', { price: rubMinor(selected.total_display || selected.cost_display) })
              : t('Pick a pack')}
          </button>
          {selected && balanceEnough && (
            <button
              type="button"
              disabled={busy}
              onClick={() => fromBalance.mutate()}
              className="w-full rounded-xl bg-white/[0.06] py-3 text-sm font-bold text-foreground flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
            >
              {fromBalance.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('Pay from balance price', { price: rubMinor(selected.cost_display) })}
            </button>
          )}
        </>
      )}
    </BottomSheet>
  );
}
